import { Button, Chip, Skeleton } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useReducedMotion } from "framer-motion";
import { memo, useEffect, useRef, useState } from "react";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";
import { SummaryChips } from "@funnel-ui/primitives/SummaryChips";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

export type RevealGateTile = {
  imageUrl: string;
  label?: string;
  locked?: boolean;
  badge?: string;
};

type RevealGateProps = {
  headline: string;
  description?: string;
  eyebrow?: string;
  tiles: RevealGateTile[];
  chips?: string[];
  ctaLabel?: string;
  onCta?: () => void;
  loginPrompt?: string;
  loginLabel?: string;
  onLogin?: () => void;
  /**
   * Initial delay before the reveal animation starts (ms). Defaults to 800.
   * Set to 0 to reveal immediately on mount.
   */
  revealDelayMs?: number;
  /**
   * Duration of each tile's unblur animation (ms). Defaults to 2000.
   */
  revealDurationMs?: number;
  /**
   * Stagger delay between each tile starting its reveal (ms). Defaults to 400.
   */
  staggerMs?: number;
  className?: string;
  testId?: string;
};

// ---------------------------------------------------------------------------
// Progressive reveal hook — rAF-driven blur from blurStart → 0 with easeOut.
// ---------------------------------------------------------------------------
const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

const useTileReveal = (
  startMs: number,
  durationMs: number,
  blurStart: number,
  prefersReduced: boolean,
): { blur: number; scale: number; progress: number } => {
  const [progress, setProgress] = useState(prefersReduced ? 1 : 0);
  const startTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number>(0);
  const delayRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (prefersReduced) {
      setProgress(1);
      return;
    }
    if (startMs <= 0 && durationMs <= 0) {
      setProgress(1);
      return;
    }
    if (durationMs <= 0) {
      delayRef.current = setTimeout(() => setProgress(1), startMs);
      return () => clearTimeout(delayRef.current);
    }

    const begin = (): void => {
      startTimeRef.current = null;
      const step = (timestamp: number): void => {
        if (startTimeRef.current === null) startTimeRef.current = timestamp;
        const elapsed = timestamp - startTimeRef.current;
        const raw = Math.min(elapsed / durationMs, 1);
        setProgress(easeOutCubic(raw));
        if (raw < 1) {
          rafRef.current = requestAnimationFrame(step);
        }
      };
      rafRef.current = requestAnimationFrame(step);
    };

    if (startMs > 0) {
      delayRef.current = setTimeout(begin, startMs);
    } else {
      begin();
    }

    return () => {
      clearTimeout(delayRef.current);
      cancelAnimationFrame(rafRef.current);
    };
  }, [startMs, durationMs, prefersReduced]);

  const blur = blurStart * (1 - progress);
  const scale = 1 + 0.08 * (1 - progress);
  return { blur, scale, progress };
};

const TileCard = ({
  tile,
  revealDelayMs,
  revealDurationMs,
  prefersReduced,
}: {
  tile: RevealGateTile;
  revealDelayMs: number;
  revealDurationMs: number;
  prefersReduced: boolean;
}) => {
  const [loaded, setLoaded] = useState(false);
  const locked = tile.locked !== false;
  const blurStart = locked ? 20 : 14;
  const { blur, scale, progress } = useTileReveal(
    revealDelayMs,
    revealDurationMs,
    blurStart,
    prefersReduced || locked,
  );
  // Locked tiles stay permanently blurred — ignore animation progress.
  const effectiveBlur = locked ? blurStart : blur;
  const effectiveScale = locked ? 1 + 0.08 : scale;
  const done = !locked && progress >= 1;
  const lockOpacity = locked ? 1 : Math.max(0, 1 - progress * 1.5);

  return (
    <div
      className="relative aspect-[3/4] overflow-hidden rounded-2xl"
      style={{ background: "hsl(var(--brand-bg-foreground) / 0.08)" }}
    >
      {!loaded && <Skeleton aria-hidden="true" className="absolute inset-0 h-full w-full" />}
      <ResponsiveAsset
        src={tile.imageUrl}
        alt={tile.label ?? ""}
        loading="eager"
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className="h-full w-full object-cover transition-opacity duration-200"
        style={{
          opacity: loaded ? 1 : 0.001,
          filter: `blur(${effectiveBlur}px)`,
          transform: `scale(${effectiveScale})`,
          willChange: effectiveBlur > 0 ? "filter, transform" : "auto",
        }}
      />
      {locked && lockOpacity > 0 && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center bg-black/30"
          style={{ opacity: lockOpacity }}
        >
          <Icon icon="solar:lock-bold" width={24} height={24} className="text-white/80" />
        </div>
      )}
      {!locked && tile.badge && done && (
        <span className="absolute left-2 top-2 z-20">
          <Chip color="accent" size="sm" variant="primary" className="shadow-md">
            {tile.badge}
          </Chip>
        </span>
      )}
      {tile.label && !locked && done && (
        <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/70 to-transparent p-2">
          <span className="text-xs font-medium text-white">{tile.label}</span>
        </div>
      )}
    </div>
  );
};

const RevealGateImpl = ({
  headline,
  description,
  eyebrow,
  tiles,
  chips,
  ctaLabel = "Create free account to unlock",
  onCta,
  loginPrompt = "Already have one?",
  loginLabel = "Log in",
  onLogin,
  revealDelayMs = 800,
  revealDurationMs = 2000,
  staggerMs = 400,
  className,
  testId,
}: RevealGateProps) => {
  const prefersReduced = !!useReducedMotion();
  const unlockedCount = tiles.filter((t) => t.locked === false).length;
  const totalAnimMs =
    prefersReduced || unlockedCount === 0
      ? 0
      : revealDelayMs + (unlockedCount - 1) * staggerMs + revealDurationMs;
  const [allDone, setAllDone] = useState(prefersReduced);

  useEffect(() => {
    if (prefersReduced) {
      setAllDone(true);
      return;
    }
    const t = setTimeout(() => setAllDone(true), totalAnimMs + 100);
    return () => clearTimeout(t);
  }, [totalAnimMs, prefersReduced]);

  // Only non-locked tiles participate in stagger ordering.
  const unlockedIndices = tiles
    .map((tile, i) => ({ locked: tile.locked !== false, i }))
    .filter(({ locked }) => !locked);

  const tileDelays = new Array<number>(tiles.length).fill(0);
  unlockedIndices.forEach(({ i }, order) => {
    tileDelays[i] = revealDelayMs + order * staggerMs;
  });

  return (
    <section data-testid={testId} className={`flex flex-col items-center gap-6 ${className ?? ""}`}>
      <StepHeading title={headline} description={description} eyebrow={eyebrow} align="center" />

      <div className="grid w-full grid-cols-3 gap-2.5">
        {tiles.map((tile, i) => (
          <TileCard
            key={i}
            tile={tile}
            revealDelayMs={tileDelays[i]!}
            revealDurationMs={revealDurationMs}
            prefersReduced={prefersReduced}
          />
        ))}
      </div>

      {chips && chips.length > 0 && (
        <div
          style={{
            opacity: allDone ? 1 : 0,
            transform: allDone ? "translateY(0)" : "translateY(12px)",
            pointerEvents: allDone ? "auto" : "none",
            transition: prefersReduced
              ? "none"
              : "opacity 500ms ease-out, transform 500ms ease-out",
          }}
        >
          <SummaryChips chips={chips} prefersReduced={prefersReduced} ariaLabel="Match summary" />
        </div>
      )}

      <div
        className="flex w-full flex-col items-center gap-3"
        style={{
          opacity: allDone ? 1 : 0,
          transform: allDone ? "translateY(0)" : "translateY(16px)",
          pointerEvents: allDone ? "auto" : "none",
          transition: prefersReduced
            ? "none"
            : "opacity 600ms 100ms ease-out, transform 600ms 100ms ease-out",
        }}
      >
        <Button variant="primary" size="lg" fullWidth onPress={onCta} className="rounded-full">
          {ctaLabel}
        </Button>
        {onLogin && loginLabel && (
          <p className="m-0 text-center text-sm" style={{ color: "hsl(var(--hero-fg-muted))" }}>
            {loginPrompt}{" "}
            <button
              type="button"
              onClick={onLogin}
              className="cursor-pointer border-none bg-transparent p-0 font-medium underline"
              style={{ color: "hsl(var(--brand-primary))" }}
            >
              {loginLabel}
            </button>
          </p>
        )}
      </div>
    </section>
  );
};

export const RevealGate = memo(RevealGateImpl);

export default RevealGate;
